import React, { createContext, useState, useContext } from 'react';

const initialFileData = {
  file: null,
  data: null,
  processedData: null,
  classifications: null,
  selectedColumns: [],
  s3Upload: {
    isUploaded: false,
    location: null,
    key: null,
    error: null
  },
  apiNotification: {
    isNotified: false,
    response: null,
    error: null
  },
  processingStatus: {
    isProcessing: false,
    progress: 0,
    currentStep: '',
  },
};

const FileDataContext = createContext(undefined);

export const useFileData = () => {
  const context = useContext(FileDataContext);
  if (!context) {
    throw new Error('useFileData must be used within a FileDataProvider');
  }
  return context;
};

export const FileDataProvider = ({ children }) => {
  const [fileData, setFileData] = useState(initialFileData);

  const setFile = (file) => {
    setFileData((prev) => ({ ...prev, file }));
  };

  const setData = (data) => {
    setFileData((prev) => ({ ...prev, data }));
  };

  const setProcessedData = (processedData) => {
    setFileData((prev) => ({ ...prev, processedData }));
  };

  const setClassifications = (classifications) => {
    setFileData((prev) => ({ ...prev, classifications }));
  };

  const setSelectedColumns = (columns) => {
    setFileData((prev) => ({ ...prev, selectedColumns: columns }));
  };

  const setS3UploadSuccess = (location, key) => {
    setFileData((prev) => ({
      ...prev,
      s3Upload: { isUploaded: true, location, key, error: null }
    }));
  };

  const setS3UploadError = (error) => {
    setFileData((prev) => ({
      ...prev,
      s3Upload: { ...prev.s3Upload, isUploaded: false, error }
    }));
  };

  const setApiNotificationSuccess = (response) => {
    setFileData((prev) => ({
      ...prev,
      apiNotification: { isNotified: true, response, error: null }
    }));
  };

  const setApiNotificationError = (error) => {
    setFileData((prev) => ({
      ...prev,
      apiNotification: { ...prev.apiNotification, isNotified: false, error }
    }));
  };

  const startProcessing = () => {
    setFileData((prev) => ({
      ...prev,
      processingStatus: { isProcessing: true, progress: 0, currentStep: 'Initializing...' },
    }));
  };

  const updateProcessingProgress = (progress, currentStep) => {
    setFileData((prev) => ({
      ...prev,
      processingStatus: { ...prev.processingStatus, progress, currentStep },
    }));
  };

  const completeProcessing = () => {
    setFileData((prev) => ({
      ...prev,
      processingStatus: { isProcessing: false, progress: 100, currentStep: 'Complete' },
    }));
  };

  const resetFileData = () => {
    setFileData(initialFileData);
  };

  const value = {
    fileData,
    setFile,
    setData,
    setProcessedData,
    setClassifications,
    setSelectedColumns,
    setS3UploadSuccess,
    setS3UploadError,
    setApiNotificationSuccess,
    setApiNotificationError,
    startProcessing,
    updateProcessingProgress,
    completeProcessing,
    resetFileData,
  };

  return <FileDataContext.Provider value={value}>{children}</FileDataContext.Provider>;
};
